import axios, {
  AxiosError, AxiosInstance, AxiosResponse, Method,
} from 'axios';
import { Headers } from './enums';
import { Envs, RequestConfig } from './types';
import { BASE_URL } from './constants';
import { setupTokenRefreshInterceptor } from './interceptors';

export default class Http {
  private static env: Envs = (process.env.GAMIPHY_ENV ?? process.env.CORETAVA_ENV);

  public static axiosInstance: AxiosInstance = axios.create({
    baseURL: BASE_URL[this.env],
    headers: {
      [Headers.CONTENT_TYPE]: 'application/json',
    },
    withCredentials: true,
  });

  public static setAuthorization(token: string): void {
    this.axiosInstance.defaults.headers.common[Headers.AUTHORIZATION] = `Bearer ${token}`;
  }

  public static setGamixAppId(appId: string): void {
    this.axiosInstance.defaults.headers.common[Headers.GAMIX_APP_ID] = appId;
  }

  public static getGamixAppId(): string | undefined {
    const appId = this.axiosInstance.defaults.headers.common[Headers.GAMIX_APP_ID];

    return typeof appId === 'string' ? appId : undefined;
  }

  public static onUnauthorized(cb: (error: AxiosError) => void): void {
    this.axiosInstance.interceptors.response.use(undefined, (error: AxiosError) => {
      if (error.response?.status === 401) {
        cb(error);
      }
      return Promise.reject(error);
    });
  }

  /**
   * Enable automatic token refresh on 401 errors
   * @param onRefreshFailed - Callback when token refresh fails
   * @param onRefreshSuccess - Callback when token refresh succeeds
   * @param enableCredentials - Whether to send credentials (cookies) with requests
   */
  public static enableTokenRefresh(
    onRefreshFailed?: (error: AxiosError) => void,
    onRefreshSuccess?: (accessToken: string, refreshToken: string) => void,
    enableCredentials: boolean = true,
  ): void {
    setupTokenRefreshInterceptor(this.axiosInstance, {
      getAppId: () => this.getGamixAppId(),
      enableCredentials,
      onRefreshSuccess,
      onRefreshFailed,
    });
  }

  public static async request<Response>(requestConfig: RequestConfig & { method: Method }): Promise<AxiosResponse<Response>> {
    return this.axiosInstance.request<Response>({
      method: requestConfig.method,
      url: requestConfig.url,
      params: requestConfig.query,
      headers: requestConfig.headers,
      data: requestConfig.payload,
      responseType: requestConfig.responseType ? requestConfig.responseType : 'json',
    });
  }

  public static async get<Response>(requestConfig: RequestConfig): Promise<AxiosResponse<Response>> {
    return Http.request({
      ...requestConfig,
      method: 'GET',
    });
  }

  public static async post<Response>(requestConfig: RequestConfig): Promise<AxiosResponse<Response>> {
    return Http.request({
      ...requestConfig,
      method: 'POST',
    });
  }

  public static async put<Response>(requestConfig: RequestConfig): Promise<AxiosResponse<Response>> {
    return Http.request({
      ...requestConfig,
      method: 'PUT',
    });
  }

  public static async delete<Response>(requestConfig: RequestConfig): Promise<AxiosResponse<Response>> {
    return Http.request({
      ...requestConfig,
      method: 'DELETE',
    });
  }

  public static async patch<Response>(requestConfig: RequestConfig): Promise<AxiosResponse<Response>> {
    return Http.request({
      ...requestConfig,
      method: 'PATCH',
    });
  }

  public static getBaseUrl(): string | undefined {
    return this.axiosInstance.defaults.baseURL;
  }

  public static getStaticUri(): string {
    switch (this.env) {
      case 'prod':
        return 'https://static.coretava.com';
      case 'staging':
        return 'https://static.staging.coretava.com';
      default:
        return 'https://static.dev.coretava.com';
    }
  }

  public static buildUri(domain: string): string {
    switch (this.env) {
      case 'prod':
        return `https://${domain}.coretava.com`;
      case 'staging':
        return `https://${domain}.staging.coretava.com`;
      default:
        return `https://${domain}.dev.coretava.com`;
    }
  }
}
